// super-excalidraw: the unified "hub" modal that replaces the old flat
// MyDiagramsDialog. One place to browse and manage everything saved on the
// server:
//   • Diagrams — folder tree + thumbnail grid, open / rename / delete / move
//   • Media    — personal image library, insert / copy-URL / delete
//   • Libraries — server-synced Excalidraw stencils, describe / delete
//
// Presentation only + the thin data/* clients. Styled entirely with
// Excalidraw's own CSS custom properties (var(--color-*)) so it matches the
// app chrome and follows light/dark theme for free.

import React, { useEffect, useMemo, useRef, useState } from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";

import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import {
  listDiagrams,
  getMeta,
  renameDiagram,
  deleteDiagram,
  diagramUrl,
  thumbnailUrl,
  type DashboardItem,
} from "../data/dashboard";
import {
  listFolders,
  createFolder,
  renameFolder,
  deleteFolder,
  moveDiagramToFolder,
  listMediaFolders,
  createMediaFolder,
  renameMediaFolder,
  deleteMediaFolder,
  moveMediaToFolder,
  listLibraryFolders,
  createLibraryFolder,
  renameLibraryFolder,
  deleteLibraryFolder,
  moveLibraryToFolder,
  type Folder,
} from "../data/folders";
import {
  fetchMediaLibrary,
  uploadMedia,
  patchMedia,
  deleteMedia,
  mediaUrl,
  type MediaItemDTO,
} from "../data/media";
import {
  fetchLibrary,
  putLibrary,
  patchLibraryItem,
  type LibraryItemDTO,
} from "../data/library";
import { searchGifs, trendGifs, type GiphyGif } from "../data/giphy";

import "./LibraryHubDialog.scss";

type Tab = "diagrams" | "media" | "libraries" | "gifs";

type Props = {
  onClose: () => void;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onInsertMedia?: (item: MediaItemDTO) => void;
  initialTab?: Tab;
};

// Sentinel folder ids for the two pseudo-folders in the rail.
const ALL = "__all__";
const UNFILED = "__unfiled__";

// ─── Shared folder UI ─────────────────────────────────────────────────────────
// One rail used by the Media and Libraries tabs (the Diagrams tab predates this
// and keeps its own inline copy). Pure presentation — the parent owns the data
// and the create/rename/delete/move handlers.

const FolderRail: React.FC<{
  folders: Folder[];
  selected: string;
  counts: Record<string, number>;
  allLabel: string;
  showUnfiled?: boolean;
  onSelect: (id: string) => void;
  onCreate: (name: string, parentId: string) => Promise<void> | void;
  onRename: (f: Folder) => void;
  onDelete: (f: Folder) => void;
}> = ({
  folders,
  selected,
  counts,
  allLabel,
  showUnfiled = true,
  onSelect,
  onCreate,
  onRename,
  onDelete,
}) => {
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const byId = useMemo(() => new Map(folders.map((x) => [x.id, x])), [folders]);

  const depthOf = (f: Folder): number => {
    let d = 0;
    let cur: Folder | undefined = f;
    let guard = 0;
    while (cur?.parentId && guard++ < 12) {
      cur = byId.get(cur.parentId);
      d++;
    }
    return d;
  };

  const create = async () => {
    const name = newName.trim();
    if (!name) {
      return;
    }
    setCreating(true);
    try {
      const parentId =
        selected === ALL || selected === UNFILED ? "" : selected;
      await onCreate(name, parentId);
      setNewName("");
    } finally {
      setCreating(false);
    }
  };

  return (
    <aside className="hub-diagrams__rail">
      <button
        type="button"
        className={`hub-rail__item ${selected === ALL ? "hub-rail__item--active" : ""}`}
        onClick={() => onSelect(ALL)}
      >
        <span className="hub-rail__label">{allLabel}</span>
        <span className="hub-rail__count">{counts[ALL] ?? 0}</span>
      </button>

      {showUnfiled && (counts[UNFILED] ?? 0) > 0 && (
        <button
          type="button"
          className={`hub-rail__item ${selected === UNFILED ? "hub-rail__item--active" : ""}`}
          onClick={() => onSelect(UNFILED)}
        >
          <span className="hub-rail__label">Unfiled</span>
          <span className="hub-rail__count">{counts[UNFILED] ?? 0}</span>
        </button>
      )}

      <div className="hub-rail__divider" />

      <div className="hub-rail__folders">
        {folders.map((f) => (
          <div
            key={f.id}
            className={`hub-rail__item hub-rail__folder ${selected === f.id ? "hub-rail__item--active" : ""}`}
            style={{ paddingLeft: 10 + depthOf(f) * 14 }}
          >
            <button
              type="button"
              className="hub-rail__folderbtn"
              onClick={() => onSelect(f.id)}
              title={f.name}
            >
              <span className="hub-rail__label">📁 {f.name}</span>
              <span className="hub-rail__count">{counts[f.id] ?? 0}</span>
            </button>
            <span className="hub-rail__folderactions">
              <button
                type="button"
                title="Rename folder"
                onClick={() => onRename(f)}
              >
                ✎
              </button>
              <button
                type="button"
                title="Delete folder"
                onClick={() => onDelete(f)}
              >
                ×
              </button>
            </span>
          </div>
        ))}
      </div>

      <div className="hub-rail__newfolder">
        <input
          value={newName}
          placeholder="New folder…"
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              create();
            }
          }}
        />
        <button
          type="button"
          disabled={creating || !newName.trim()}
          onClick={create}
        >
          +
        </button>
      </div>
    </aside>
  );
};

// Compact "move to folder" dropdown shown on each media card / library row.
const MoveSelect: React.FC<{
  value: string;
  folders: Folder[];
  disabled?: boolean;
  onChange: (folderId: string) => void;
}> = ({ value, folders, disabled, onChange }) => (
  <select
    className="hub-move"
    value={value || ""}
    disabled={disabled}
    title="Move to folder"
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">Unfiled</option>
    {folders.map((f) => (
      <option key={f.id} value={f.id}>
        {f.name}
      </option>
    ))}
  </select>
);

export const LibraryHubDialog: React.FC<Props> = ({
  onClose,
  excalidrawAPI,
  onInsertMedia,
  initialTab,
}) => {
  const [tab, setTab] = useState<Tab>(initialTab ?? "diagrams");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedQuery(val), 350);
  };

  const isSearching = debouncedQuery.trim().length > 0;

  return (
    <Dialog onCloseRequest={onClose} title="" size="wide">
      <div className="hub">
        <div className="hub__searchbar">
          <input
            className="hub__searchinput"
            type="search"
            placeholder="Search diagrams, media, stencils, GIFs…"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {!isSearching && (
          <nav className="hub__tabs" role="tablist">
            {(
              [
                ["diagrams", "Diagrams"],
                ["media", "Media"],
                ["libraries", "Libraries"],
                ["gifs", "GIFs"],
              ] as [Tab, string][]
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                className={`hub__tab ${tab === key ? "hub__tab--active" : ""}`}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </nav>
        )}

        <div className="hub__body">
          {isSearching ? (
            <UnifiedSearchPane
              query={debouncedQuery}
              excalidrawAPI={excalidrawAPI}
              onInsertMedia={onInsertMedia}
              onClose={onClose}
            />
          ) : (
            <>
              {tab === "diagrams" && (
                <DiagramsTab excalidrawAPI={excalidrawAPI} onClose={onClose} />
              )}
              {tab === "media" && (
                <MediaTab onInsertMedia={onInsertMedia} onClose={onClose} />
              )}
              {tab === "libraries" && <LibrariesTab />}
              {tab === "gifs" && (
                <GifsTab excalidrawAPI={excalidrawAPI} onClose={onClose} />
              )}
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
};

// ─── Diagrams ───────────────────────────────────────────────────────────────

const DiagramsTab: React.FC<{
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onClose: () => void;
}> = ({ onClose }) => {
  const [items, setItems] = useState<DashboardItem[] | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selected, setSelected] = useState<string>(ALL);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);

  const refresh = async () => {
    setError(null);
    try {
      const [diags, fldrs] = await Promise.all([listDiagrams(), listFolders()]);
      diags.sort((a, b) => b.createdAt - a.createdAt);
      setItems(diags);
      setFolders(fldrs);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setItems([]);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  // Diagram count per folder id (for the rail badges + "Unfiled").
  const counts = useMemo(() => {
    const c: Record<string, number> = { [ALL]: 0, [UNFILED]: 0 };
    for (const it of items ?? []) {
      c[ALL] = (c[ALL] ?? 0) + 1;
      const fid = it.folderId || "";
      if (!fid) {
        c[UNFILED] = (c[UNFILED] ?? 0) + 1;
      } else {
        c[fid] = (c[fid] ?? 0) + 1;
      }
    }
    return c;
  }, [items]);

  const visible = useMemo(() => {
    const all = items ?? [];
    if (selected === ALL) {
      return all;
    }
    return all.filter((i) => i.folderId === selected);
  }, [items, selected]);

  // Depth of each folder for tree indentation (parentId chain).
  const depthOf = (f: Folder): number => {
    let d = 0;
    let cur: Folder | undefined = f;
    const byId = new Map(folders.map((x) => [x.id, x]));
    while (cur?.parentId) {
      cur = byId.get(cur.parentId);
      d++;
      if (d > 12) {
        break; // cycle guard
      }
    }
    return d;
  };

  const onOpen = async (item: DashboardItem) => {
    setBusyId(item.id);
    try {
      const full = await getMeta(item.id);
      const target = diagramUrl(full);
      onClose();
      // Flush the current diagram before navigating (autosave is debounced).
      try {
        const flush = (window as any).__superExcalidrawFlushAutosave;
        if (typeof flush === "function") {
          await flush();
        }
      } catch {
        // best-effort
      }
      window.location.href = target;
      window.location.reload();
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setBusyId(null);
    }
  };

  const onDelete = async (item: DashboardItem) => {
    if (!confirm(`Delete "${item.name || item.id}"? This cannot be undone.`)) {
      return;
    }
    setBusyId(item.id);
    try {
      await deleteDiagram(item.id);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  };

  const saveRename = async (id: string) => {
    setBusyId(id);
    try {
      await renameDiagram(id, draftName.trim() || "Untitled");
      setEditingId(null);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onMove = async (item: DashboardItem, folderId: string) => {
    setBusyId(item.id);
    try {
      await moveDiagramToFolder(item.id, folderId === UNFILED ? "" : folderId);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) {
      return;
    }
    setCreatingFolder(true);
    try {
      // Nest under the currently-selected real folder, else create at root.
      const parentId =
        selected === ALL || selected === UNFILED ? "" : selected;
      await createFolder(name, parentId);
      setNewFolderName("");
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setCreatingFolder(false);
    }
  };

  const onRenameFolder = async (f: Folder) => {
    const name = prompt("Rename folder", f.name);
    if (name == null || !name.trim()) {
      return;
    }
    try {
      await renameFolder(f.id, name.trim());
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
  };

  const onDeleteFolder = async (f: Folder) => {
    if (
      !confirm(
        `Delete folder "${f.name}"? Diagrams inside move to its parent (nothing is deleted).`,
      )
    ) {
      return;
    }
    try {
      await deleteFolder(f.id);
      if (selected === f.id) {
        setSelected(ALL);
      }
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
  };

  return (
    <div className="hub-diagrams">
      <aside className="hub-diagrams__rail">
        <button
          type="button"
          className={`hub-rail__item ${selected === ALL ? "hub-rail__item--active" : ""}`}
          onClick={() => setSelected(ALL)}
        >
          <span className="hub-rail__label">All diagrams</span>
          <span className="hub-rail__count">{counts[ALL] ?? 0}</span>
        </button>

        <div className="hub-rail__divider" />

        <div className="hub-rail__folders">
          {folders.map((f) => (
            <div
              key={f.id}
              className={`hub-rail__item hub-rail__folder ${selected === f.id ? "hub-rail__item--active" : ""}`}
              style={{ paddingLeft: 10 + depthOf(f) * 14 }}
            >
              <button
                type="button"
                className="hub-rail__folderbtn"
                onClick={() => setSelected(f.id)}
                title={f.name}
              >
                <span className="hub-rail__label">📁 {f.name}</span>
                <span className="hub-rail__count">{counts[f.id] ?? 0}</span>
              </button>
              <span className="hub-rail__folderactions">
                <button
                  type="button"
                  title="Rename folder"
                  onClick={() => onRenameFolder(f)}
                >
                  ✎
                </button>
                <button
                  type="button"
                  title="Delete folder"
                  onClick={() => onDeleteFolder(f)}
                >
                  ×
                </button>
              </span>
            </div>
          ))}
        </div>

        <div className="hub-rail__newfolder">
          <input
            value={newFolderName}
            placeholder="New folder…"
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onCreateFolder();
              }
            }}
          />
          <button
            type="button"
            disabled={creatingFolder || !newFolderName.trim()}
            onClick={onCreateFolder}
          >
            +
          </button>
        </div>
      </aside>

      <section className="hub-diagrams__main">
        {error && <div className="hub__error">Error: {error}</div>}
        {items === null && <div className="hub__loading">Loading…</div>}
        {items?.length === 0 && (
          <div className="hub__empty">
            No saved diagrams yet. Draw something, then use{" "}
            <em>Share → Shareable link</em> (or it auto-saves) to see it here.
          </div>
        )}
        {items !== null && items.length > 0 && visible.length === 0 && (
          <div className="hub__empty">This folder is empty.</div>
        )}

        <ul className="hub-grid">
          {visible.map((item) => (
            <li key={item.id} className="hub-card">
              <div
                className="hub-card__thumb"
                onClick={() => onOpen(item)}
                role="button"
                title="Open diagram"
              >
                {/* Always try the thumbnail endpoint — the server falls back
                    to a frame PNG for API-created diagrams, so a missing
                    .thumb.png isn't necessarily "no preview". onError swaps
                    to the placeholder. */}
                <img
                  src={thumbnailUrl(item.id)}
                  alt={item.name || item.id}
                  loading="lazy"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display =
                      "none";
                    const ph = e.currentTarget
                      .nextElementSibling as HTMLElement | null;
                    if (ph) {
                      ph.style.display = "flex";
                    }
                  }}
                />
                <div
                  className="hub-card__thumb-ph"
                  style={{ display: "none" }}
                >
                  no preview
                </div>
              </div>

              <div className="hub-card__meta">
                {editingId === item.id ? (
                  <input
                    autoFocus
                    className="hub-card__rename"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        saveRename(item.id);
                      } else if (e.key === "Escape") {
                        setEditingId(null);
                      }
                    }}
                    onBlur={() => saveRename(item.id)}
                  />
                ) : (
                  <div
                    className="hub-card__name"
                    onDoubleClick={() => {
                      setEditingId(item.id);
                      setDraftName(item.name || "");
                    }}
                    title="Double-click to rename"
                  >
                    {item.name || "Untitled"}
                  </div>
                )}
                <div className="hub-card__date">
                  {new Date(item.createdAt * 1000).toLocaleDateString()}
                </div>
              </div>

              <div className="hub-card__actions">
                <button
                  type="button"
                  disabled={busyId === item.id}
                  onClick={() => onOpen(item)}
                >
                  Open
                </button>
                <select
                  className="hub-card__move"
                  title="Move to folder"
                  value={item.folderId || UNFILED}
                  disabled={busyId === item.id}
                  onChange={(e) => onMove(item, e.target.value)}
                >
                  <option value={UNFILED}>No folder</option>
                  {folders.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="hub-card__danger"
                  disabled={busyId === item.id}
                  onClick={() => onDelete(item)}
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

// ─── Media ────────────────────────────────────────────────────────────────

const MediaTab: React.FC<{
  onInsertMedia?: (item: MediaItemDTO) => void;
  onClose: () => void;
}> = ({ onInsertMedia, onClose }) => {
  const [items, setItems] = useState<MediaItemDTO[] | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selected, setSelected] = useState<string>(ALL);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState("");
  const [draftDesc, setDraftDesc] = useState("");

  const refresh = async () => {
    setError(null);
    try {
      const [lib, fldrs] = await Promise.all([
        fetchMediaLibrary(),
        listMediaFolders(),
      ]);
      const list = (lib.items ?? []).slice();
      list.sort((a, b) => (b.addedAt ?? 0) - (a.addedAt ?? 0));
      setItems(list);
      setFolders(fldrs);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setItems([]);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = { [ALL]: 0, [UNFILED]: 0 };
    for (const it of items ?? []) {
      c[ALL] = (c[ALL] ?? 0) + 1;
      const fid = it.folderId || "";
      c[fid ? fid : UNFILED] = (c[fid ? fid : UNFILED] ?? 0) + 1;
    }
    return c;
  }, [items]);

  const visible = useMemo(() => {
    const all = items ?? [];
    if (selected === ALL) {
      return all;
    }
    if (selected === UNFILED) {
      return all.filter((i) => !i.folderId);
    }
    return all.filter((i) => i.folderId === selected);
  }, [items, selected]);

  const onMove = async (item: MediaItemDTO, folderId: string) => {
    setBusy(true);
    try {
      await moveMediaToFolder(item.id, folderId);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const onRenameFolder = async (f: Folder) => {
    const name = prompt("Rename folder", f.name);
    if (name == null || !name.trim()) {
      return;
    }
    try {
      await renameMediaFolder(f.id, name.trim());
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
  };

  const onDeleteFolder = async (f: Folder) => {
    if (
      !confirm(
        `Delete folder "${f.name}"? Items inside move to its parent (nothing is deleted).`,
      )
    ) {
      return;
    }
    try {
      await deleteMediaFolder(f.id);
      if (selected === f.id) {
        setSelected(ALL);
      }
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
  };

  const onUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }
    setBusy(true);
    try {
      for (const file of Array.from(files)) {
        await uploadMedia({
          file,
          name: file.name.replace(/\.[^/.]+$/, ""),
          description: "",
        });
      }
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (item: MediaItemDTO) => {
    if (!confirm(`Delete media "${item.name || item.id}"?`)) {
      return;
    }
    setBusy(true);
    try {
      await deleteMedia(item.id);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (item: MediaItemDTO) => {
    setEditingId(item.id);
    setDraftName(item.name || "");
    setDraftDesc(item.description || "");
  };

  const saveEdit = async (item: MediaItemDTO) => {
    setBusy(true);
    try {
      await patchMedia(item.id, {
        name: draftName.trim(),
        description: draftDesc.trim(),
      });
      setEditingId(null);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const onCopyUrl = async (item: MediaItemDTO) => {
    const url = new URL(mediaUrl(item), window.location.origin).toString();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      setError("clipboard unavailable");
    }
  };

  return (
    <div className="hub-diagrams">
      <FolderRail
        folders={folders}
        selected={selected}
        counts={counts}
        allLabel="All media"
        onSelect={setSelected}
        onCreate={async (name, parentId) => {
          await createMediaFolder(name, parentId);
          await refresh();
        }}
        onRename={onRenameFolder}
        onDelete={onDeleteFolder}
      />
      <section className="hub-diagrams__main">
        <div className="hub-media">
          <div className="hub-media__bar">
            <label className="hub-media__upload">
              {busy ? "Uploading…" : "Upload image"}
              <input
                type="file"
                accept="image/*"
                multiple
                disabled={busy}
                onChange={(e) => onUpload(e.target.files)}
              />
            </label>
            <span className="hub-media__hint">
              Reusable images. Insert into the canvas or copy a stable URL for
              docs.
            </span>
          </div>

          {error && <div className="hub__error">Error: {error}</div>}
          {items === null && <div className="hub__loading">Loading…</div>}
          {items?.length === 0 && (
            <div className="hub__empty">
              No media yet. Upload an image above.
            </div>
          )}
          {items && items.length > 0 && visible.length === 0 && (
            <div className="hub__empty">This folder is empty.</div>
          )}

          <ul className="hub-grid">
            {visible.map((item) => (
          <li key={item.id} className="hub-card">
            <div className="hub-card__thumb hub-card__thumb--media">
              <img src={mediaUrl(item)} alt={item.name || item.id} loading="lazy" />
            </div>
            {editingId === item.id ? (
              <div className="hub-card__edit">
                <input
                  autoFocus
                  className="hub-card__rename"
                  placeholder="Name"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                />
                <textarea
                  className="hub-card__descedit"
                  placeholder="Description (AI uses this to know when to insert this image)"
                  rows={3}
                  value={draftDesc}
                  onChange={(e) => setDraftDesc(e.target.value)}
                />
                <div className="hub-card__editactions">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => saveEdit(item)}
                  >
                    Save
                  </button>
                  <button type="button" onClick={() => setEditingId(null)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="hub-card__meta">
                  <div className="hub-card__name" title={item.name}>
                    {item.name || "Untitled"}
                  </div>
                  <div
                    className="hub-card__desc"
                    title={item.description || ""}
                  >
                    {item.description || (
                      <span className="hub-card__desc--empty">
                        No description
                      </span>
                    )}
                  </div>
                  <div className="hub-card__date">
                    {item.width && item.height
                      ? `${item.width}×${item.height}`
                      : item.mimeType || ""}
                  </div>
                </div>
                <div className="hub-card__actions">
                  {onInsertMedia && (
                    <button
                      type="button"
                      onClick={() => {
                        onInsertMedia(item);
                        onClose();
                      }}
                    >
                      Insert
                    </button>
                  )}
                  <button type="button" onClick={() => onCopyUrl(item)}>
                    {copiedId === item.id ? "Copied" : "Copy URL"}
                  </button>
                  <button type="button" onClick={() => startEdit(item)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="hub-card__danger"
                    onClick={() => onDelete(item)}
                  >
                    Delete
                  </button>
                  <MoveSelect
                    value={item.folderId || ""}
                    folders={folders}
                    disabled={busy}
                    onChange={(fid) => onMove(item, fid)}
                  />
                </div>
              </>
            )}
          </li>
        ))}
          </ul>
        </div>
      </section>
    </div>
  );
};

// ─── Libraries ──────────────────────────────────────────────────────────────

const LibrariesTab: React.FC = () => {
  const [items, setItems] = useState<LibraryItemDTO[] | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selected, setSelected] = useState<string>(ALL);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<
    Record<string, { name: string; description: string }>
  >({});
  const [savingId, setSavingId] = useState<string | null>(null);
  // item.id -> rendered SVG data URL (built client-side via exportToSvg).
  const [previews, setPreviews] = useState<Record<string, string>>({});

  const refresh = async () => {
    setError(null);
    try {
      const [lib, fldrs] = await Promise.all([
        fetchLibrary(),
        listLibraryFolders(),
      ]);
      setItems(lib.items ?? []);
      setFolders(fldrs);
      const d: Record<string, { name: string; description: string }> = {};
      for (const it of lib.items ?? []) {
        d[it.id] = { name: it.name ?? "", description: it.description ?? "" };
      }
      setDrafts(d);
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setItems([]);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = { [ALL]: 0, [UNFILED]: 0 };
    for (const it of items ?? []) {
      c[ALL] = (c[ALL] ?? 0) + 1;
      const fid = it.folderId || "";
      c[fid ? fid : UNFILED] = (c[fid ? fid : UNFILED] ?? 0) + 1;
    }
    return c;
  }, [items]);

  const visible = useMemo(() => {
    const all = items ?? [];
    if (selected === ALL) {
      return all;
    }
    if (selected === UNFILED) {
      return all.filter((i) => !i.folderId);
    }
    return all.filter((i) => i.folderId === selected);
  }, [items, selected]);

  const onMove = async (item: LibraryItemDTO, folderId: string) => {
    setSavingId(item.id);
    try {
      await moveLibraryToFolder(item.id, folderId);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSavingId(null);
    }
  };

  const onRenameFolder = async (f: Folder) => {
    const name = prompt("Rename folder", f.name);
    if (name == null || !name.trim()) {
      return;
    }
    try {
      await renameLibraryFolder(f.id, name.trim());
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
  };

  const onDeleteFolder = async (f: Folder) => {
    if (
      !confirm(
        `Delete folder "${f.name}"? Items inside move to its parent (nothing is deleted).`,
      )
    ) {
      return;
    }
    try {
      await deleteLibraryFolder(f.id);
      if (selected === f.id) {
        setSelected(ALL);
      }
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    }
  };

  // Render a visual preview of each stencil — same idea as the right-sidebar
  // library: rasterize the item's elements to an SVG so you can SEE the shape,
  // not just its name. Done lazily after the list loads; failures are silently
  // skipped (a missing preview just shows a placeholder).
  useEffect(() => {
    if (!items || items.length === 0) {
      return;
    }
    let cancelled = false;
    (async () => {
      let exportToSvg: any;
      try {
        const mod: any = await import("@excalidraw/utils/export");
        exportToSvg = mod.exportToSvg;
      } catch {
        return; // utility unavailable — placeholders only
      }
      if (!exportToSvg) {
        return;
      }
      for (const it of items) {
        if (cancelled) {
          return;
        }
        if (previews[it.id]) {
          continue;
        }
        const els = (it.elements as any[]) ?? [];
        if (!els.length) {
          continue;
        }
        try {
          const svg: SVGSVGElement = await exportToSvg({
            elements: els,
            appState: {
              exportBackground: false,
              viewBackgroundColor: "transparent",
            },
            files: {},
            exportPadding: 6,
          });
          svg.setAttribute("width", "100%");
          svg.setAttribute("height", "100%");
          const str = new XMLSerializer().serializeToString(svg);
          const dataUrl =
            "data:image/svg+xml;base64," +
            btoa(unescape(encodeURIComponent(str)));
          if (!cancelled) {
            setPreviews((p) => ({ ...p, [it.id]: dataUrl }));
          }
        } catch {
          // skip this item's preview
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [items]);

  const onSave = async (item: LibraryItemDTO) => {
    const draft = drafts[item.id];
    if (!draft) {
      return;
    }
    setSavingId(item.id);
    try {
      await patchLibraryItem(item.id, {
        name: draft.name,
        description: draft.description,
      });
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSavingId(null);
    }
  };

  const onDelete = async (item: LibraryItemDTO) => {
    if (!confirm(`Remove library item "${item.name || item.id}"?`)) {
      return;
    }
    setSavingId(item.id);
    try {
      // No dedicated delete endpoint: re-PUT the library without this item.
      const lib = await fetchLibrary();
      lib.items = (lib.items ?? []).filter((i) => i.id !== item.id);
      await putLibrary(lib);
      await refresh();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSavingId(null);
    }
  };

  const elementTypes = (it: LibraryItemDTO): string => {
    const els = (it.elements as any[]) ?? [];
    const types = [...new Set(els.map((e) => e?.type).filter(Boolean))];
    return types.length ? types.join(", ") : "—";
  };

  return (
    <div className="hub-diagrams">
      <FolderRail
        folders={folders}
        selected={selected}
        counts={counts}
        allLabel="All stencils"
        showUnfiled={false}
        onSelect={setSelected}
        onCreate={async (name, parentId) => {
          await createLibraryFolder(name, parentId);
          await refresh();
        }}
        onRename={onRenameFolder}
        onDelete={onDeleteFolder}
      />
      <section className="hub-diagrams__main">
        <div className="hub-libs">
          <p className="hub-libs__intro">
            Server-synced Excalidraw stencils (personal + imported). Add a short
            <em> description</em> so the AI knows when to reach for each shape.
            Add new shapes from the right-toolbar Library; they sync here.
          </p>
          {error && <div className="hub__error">Error: {error}</div>}
          {items === null && <div className="hub__loading">Loading…</div>}
          {items?.length === 0 && (
            <div className="hub__empty">No library items yet.</div>
          )}
          {items && items.length > 0 && visible.length === 0 && (
            <div className="hub__empty">This folder is empty.</div>
          )}
          <ul className="hub-libs__list">
            {visible.map((item) => {
          const draft = drafts[item.id] ?? { name: "", description: "" };
          return (
            <li key={item.id} className="hub-libs__row">
              <div className="hub-libs__preview" title={item.name || item.id}>
                {previews[item.id] ? (
                  <img src={previews[item.id]} alt={item.name || "stencil"} />
                ) : (
                  <span className="hub-libs__preview-ph">▢</span>
                )}
              </div>
              <div className="hub-libs__fields">
                <input
                  className="hub-libs__name"
                  placeholder="Name (e.g. Lambda)"
                  value={draft.name}
                  onChange={(e) =>
                    setDrafts((d) => ({
                      ...d,
                      [item.id]: { ...draft, name: e.target.value },
                    }))
                  }
                />
                <textarea
                  className="hub-libs__desc"
                  placeholder="Description for AI (e.g. AWS serverless function — orange square with λ)"
                  rows={2}
                  value={draft.description}
                  onChange={(e) =>
                    setDrafts((d) => ({
                      ...d,
                      [item.id]: { ...draft, description: e.target.value },
                    }))
                  }
                />
                <div className="hub-libs__types">
                  {elementTypes(item)} ·{" "}
                  {((item.elements as any[]) ?? []).length} elements
                </div>
              </div>
              <div className="hub-libs__rowactions">
                <button
                  type="button"
                  disabled={savingId === item.id}
                  onClick={() => onSave(item)}
                >
                  {savingId === item.id ? "…" : "Save"}
                </button>
                <button
                  type="button"
                  className="hub-card__danger"
                  disabled={savingId === item.id}
                  onClick={() => onDelete(item)}
                >
                  Delete
                </button>
                <MoveSelect
                  value={item.folderId || ""}
                  folders={folders}
                  disabled={savingId === item.id}
                  onChange={(fid) => onMove(item, fid)}
                />
              </div>
            </li>
          );
        })}
          </ul>
        </div>
      </section>
    </div>
  );
};

// ─── GIFs ────────────────────────────────────────────────────────────────────

// Helper shared by GifsTab and UnifiedSearchPane for inserting a fetched GIF
// onto the Excalidraw canvas. Caps width at 480px, centers in viewport.
async function insertGifOnCanvas(
  gif: GiphyGif,
  excalidrawAPI: ExcalidrawImperativeAPI,
): Promise<void> {
  const r = await fetch(gif.url);
  if (!r.ok) throw new Error(`fetch failed: ${r.status}`);
  const blob = await r.blob();
  const dataURL: string = await new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result as string);
    reader.onerror = rej;
    reader.readAsDataURL(blob);
  });

  const fileId = `gif-${gif.id}` as any;
  excalidrawAPI.addFiles([
    { id: fileId, dataURL: dataURL as any, mimeType: "image/gif" as any, created: Date.now() },
  ]);

  const maxW = 480;
  const srcW = gif.width || 480;
  const srcH = gif.height || 360;
  const w = Math.min(maxW, srcW);
  const h = Math.round(w * (srcH / srcW));

  const appState = excalidrawAPI.getAppState();
  const cx = -appState.scrollX + (appState as any).width / 2 - w / 2;
  const cy = -appState.scrollY + (appState as any).height / 2 - h / 2;

  excalidrawAPI.updateScene({
    elements: [
      ...excalidrawAPI.getSceneElementsIncludingDeleted(),
      {
        type: "image",
        version: 1,
        versionNonce: Math.floor(Math.random() * 2147483647),
        isDeleted: false,
        id: `gif-${gif.id}-${Date.now()}`,
        fillStyle: "solid",
        strokeWidth: 1,
        strokeStyle: "solid",
        roughness: 0,
        opacity: 100,
        angle: 0,
        x: cx,
        y: cy,
        strokeColor: "transparent",
        backgroundColor: "transparent",
        width: w,
        height: h,
        seed: Math.floor(Math.random() * 2147483647),
        groupIds: [],
        frameId: null,
        roundness: null,
        boundElements: null,
        updated: Date.now(),
        link: null,
        locked: false,
        status: "loaded",
        fileId,
        scale: [1, 1],
      } as any,
    ],
  });
}

const GifsTab: React.FC<{
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onClose: () => void;
}> = ({ excalidrawAPI, onClose }) => {
  const [query, setQuery] = useState("");
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inserting, setInserting] = useState<string | null>(null);
  const [trending, setTrending] = useState(true); // showing trending vs results

  // Load trending GIFs on mount so the tab isn't empty before searching.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    trendGifs()
      .then((g) => {
        if (!cancelled) {
          setGifs(g);
          setLoading(false);
        }
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(e?.message ?? String(e));
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const doSearch = async (q: string) => {
    if (!q.trim()) {
      // Empty query: fall back to trending.
      setLoading(true);
      setError(null);
      try {
        setGifs(await trendGifs());
        setTrending(true);
      } catch (e: any) {
        setError(e?.message ?? String(e));
      } finally {
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setGifs(await searchGifs(q));
      setTrending(false);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setLoading(false);
    }
  };

  const onInsert = async (gif: GiphyGif) => {
    if (!excalidrawAPI) return;
    setInserting(gif.id);
    try {
      await insertGifOnCanvas(gif, excalidrawAPI);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setInserting(null);
    }
  };

  return (
    <div className="hub-gifs">
      <div className="hub-gifs__bar">
        <input
          className="hub-gifs__search"
          type="search"
          placeholder="Search Giphy…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") doSearch(query); }}
          autoFocus
        />
        <button
          type="button"
          className="hub-gifs__btn"
          disabled={loading || !query.trim()}
          onClick={() => doSearch(query)}
        >
          {loading ? "…" : "Search"}
        </button>
      </div>

      {error && <div className="hub__error">Error: {error}</div>}

      {loading && <div className="hub__loading">Loading…</div>}

      {!loading && gifs.length > 0 && (
        <div className="hub-gifs__heading">
          {trending ? "Trending" : `Results for "${query.trim()}"`}
        </div>
      )}

      {gifs.length === 0 && !loading && (
        <div className="hub__empty hub-gifs__empty">
          {query.trim() ? "No results." : "No GIFs to show."}
        </div>
      )}

      <ul className="hub-gifs__grid">
        {gifs.map((gif) => (
          <li key={gif.id} className="hub-gifs__item">
            <img
              src={gif.previewUrl}
              alt={gif.title}
              className="hub-gifs__preview"
              loading="lazy"
              title={gif.title}
              onClick={() => onInsert(gif)}
            />
            <button
              type="button"
              className="hub-gifs__insert"
              disabled={!!inserting}
              onClick={() => onInsert(gif)}
            >
              {inserting === gif.id ? "…" : "Insert"}
            </button>
          </li>
        ))}
      </ul>

      {gifs.length > 0 && (
        <div className="hub-gifs__attr">Powered by GIPHY</div>
      )}
    </div>
  );
};

// ─── Unified Search ──────────────────────────────────────────────────────────

// One flat, ordered result row. The left list renders these grouped by kind
// but each carries a global index so Up/Down arrows walk the whole set and the
// right pane previews whatever is selected.
type SearchRow =
  | { kind: "diagram"; key: string; label: string; diagram: DashboardItem }
  | { kind: "media"; key: string; label: string; media: MediaItemDTO }
  | { kind: "stencil"; key: string; label: string; stencil: LibraryItemDTO }
  | { kind: "gif"; key: string; label: string; gif: GiphyGif };

const KIND_LABEL: Record<SearchRow["kind"], string> = {
  diagram: "Diagrams",
  media: "Media",
  stencil: "Stencils",
  gif: "GIFs",
};

const UnifiedSearchPane: React.FC<{
  query: string;
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  onInsertMedia?: (item: MediaItemDTO) => void;
  onClose: () => void;
}> = ({ query, excalidrawAPI, onInsertMedia, onClose }) => {
  const [rows, setRows] = useState<SearchRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inserting, setInserting] = useState<string | null>(null);
  const [selected, setSelected] = useState(0);
  // Lazy stencil SVG previews, keyed by stencil id.
  const [stencilPreviews, setStencilPreviews] = useState<Record<string, string>>(
    {},
  );
  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(0);

    const q = query.trim().toLowerCase();
    Promise.all([
      listDiagrams(),
      fetchMediaLibrary(),
      fetchLibrary(),
      searchGifs(query),
    ])
      .then(([d, m, l, g]) => {
        if (cancelled) return;
        const next: SearchRow[] = [];
        d.filter((x) => (x.name || "").toLowerCase().includes(q)).forEach((x) =>
          next.push({
            kind: "diagram",
            key: `d-${x.id}`,
            label: x.name || "Untitled",
            diagram: x,
          }),
        );
        (m.items ?? [])
          .filter(
            (x) =>
              (x.name || "").toLowerCase().includes(q) ||
              (x.description || "").toLowerCase().includes(q),
          )
          .forEach((x) =>
            next.push({
              kind: "media",
              key: `m-${x.id}`,
              label: x.name || "Untitled",
              media: x,
            }),
          );
        (l.items ?? [])
          .filter(
            (x) =>
              ((x as any).name || "").toLowerCase().includes(q) ||
              ((x as any).description || "").toLowerCase().includes(q),
          )
          .forEach((x) =>
            next.push({
              kind: "stencil",
              key: `s-${x.id}`,
              label: (x as any).name || "Unnamed stencil",
              stencil: x,
            }),
          );
        g.forEach((x) =>
          next.push({
            kind: "gif",
            key: `g-${x.id}`,
            label: x.title || "GIF",
            gif: x,
          }),
        );
        setRows(next);
        setLoading(false);
      })
      .catch((e: any) => {
        if (!cancelled) {
          setError(e?.message ?? String(e));
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const openDiagram = async (item: DashboardItem) => {
    const full = await getMeta(item.id);
    onClose();
    window.location.href = diagramUrl(full);
    window.location.reload();
  };

  const insertGif = async (gif: GiphyGif) => {
    if (!excalidrawAPI) return;
    setInserting(gif.id);
    try {
      await insertGifOnCanvas(gif, excalidrawAPI);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setInserting(null);
    }
  };

  // Enter / double-click action for the selected row.
  const activate = (row: SearchRow | undefined) => {
    if (!row) return;
    if (row.kind === "diagram") openDiagram(row.diagram);
    else if (row.kind === "media" && onInsertMedia) {
      onInsertMedia(row.media);
      onClose();
    } else if (row.kind === "gif") insertGif(row.gif);
    // stencils have no insert action here — preview only.
  };

  // Arrow-key navigation. Window-level so it works while the search box still
  // holds focus; we own Up/Down/Enter and let everything else through.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (rows.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((i) => Math.min(rows.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        e.preventDefault();
        activate(rows[selected]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [rows, selected]);

  // Keep the selected row scrolled into view.
  useEffect(() => {
    rowRefs.current[selected]?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  // Build a stencil SVG preview lazily when one becomes the selection.
  useEffect(() => {
    const row = rows[selected];
    if (!row || row.kind !== "stencil") return;
    const id = (row.stencil as any).id;
    if (stencilPreviews[id]) return;
    let cancelled = false;
    (async () => {
      try {
        const mod: any = await import("@excalidraw/utils/export");
        const els = ((row.stencil as any).elements as any[]) ?? [];
        if (!els.length || !mod.exportToSvg) return;
        const svg: SVGSVGElement = await mod.exportToSvg({
          elements: els,
          appState: {
            exportBackground: false,
            viewBackgroundColor: "transparent",
          },
          files: {},
          exportPadding: 8,
        });
        svg.setAttribute("width", "100%");
        svg.setAttribute("height", "100%");
        const str = new XMLSerializer().serializeToString(svg);
        const dataUrl =
          "data:image/svg+xml;base64," +
          btoa(unescape(encodeURIComponent(str)));
        if (!cancelled) {
          setStencilPreviews((p) => ({ ...p, [id]: dataUrl }));
        }
      } catch {
        // placeholder stays
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rows, selected, stencilPreviews]);

  if (loading) return <div className="hub__loading">Searching…</div>;

  if (error) return <div className="hub__error">Error: {error}</div>;

  if (rows.length === 0) {
    return <div className="hub__empty">No results for "{query}".</div>;
  }

  const sel = rows[selected];

  // Group consecutive rows by kind for headings, while keeping global indices.
  const groups: { kind: SearchRow["kind"]; items: { row: SearchRow; idx: number }[] }[] = [];
  rows.forEach((row, idx) => {
    const last = groups[groups.length - 1];
    if (last && last.kind === row.kind) last.items.push({ row, idx });
    else groups.push({ kind: row.kind, items: [{ row, idx }] });
  });

  return (
    <div className="hub-search">
      <div className="hub-search__listpane">
        {groups.map((grp) => (
          <section key={grp.kind} className="hub-search__section">
            <div className="hub-search__heading">
              {KIND_LABEL[grp.kind]}{" "}
              <span className="hub-search__count">{grp.items.length}</span>
            </div>
            <ul className="hub-search__list">
              {grp.items.map(({ row, idx }) => (
                <li
                  key={row.key}
                  ref={(el) => {
                    rowRefs.current[idx] = el;
                  }}
                  className={`hub-search__row ${idx === selected ? "hub-search__row--active" : ""}`}
                  onClick={() => setSelected(idx)}
                  onDoubleClick={() => activate(row)}
                >
                  {row.kind === "media" && (
                    <img
                      src={mediaUrl(row.media)}
                      alt=""
                      className="hub-search__thumb"
                      loading="lazy"
                    />
                  )}
                  {row.kind === "gif" && (
                    <img
                      src={row.gif.previewUrl}
                      alt=""
                      className="hub-search__thumb"
                      loading="lazy"
                    />
                  )}
                  <span className="hub-search__name">{row.label}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <div className="hub-search__preview">
        {sel && <SearchPreview
          row={sel}
          stencilPreview={
            sel.kind === "stencil"
              ? stencilPreviews[(sel.stencil as any).id]
              : undefined
          }
          inserting={inserting}
          canInsertMedia={!!onInsertMedia}
          onOpenDiagram={openDiagram}
          onInsertMedia={(m) => {
            onInsertMedia?.(m);
            onClose();
          }}
          onInsertGif={insertGif}
        />}
        <div className="hub-search__hint">↑ ↓ to navigate · Enter to use</div>
      </div>
    </div>
  );
};

// Right-hand preview for the currently-selected search row.
const SearchPreview: React.FC<{
  row: SearchRow;
  stencilPreview?: string;
  inserting: string | null;
  canInsertMedia: boolean;
  onOpenDiagram: (d: DashboardItem) => void;
  onInsertMedia: (m: MediaItemDTO) => void;
  onInsertGif: (g: GiphyGif) => void;
}> = ({
  row,
  stencilPreview,
  inserting,
  canInsertMedia,
  onOpenDiagram,
  onInsertMedia,
  onInsertGif,
}) => {
  if (row.kind === "diagram") {
    const d = row.diagram;
    return (
      <div className="hub-search__previewinner">
        <div className="hub-search__previewmedia">
          <img
            src={thumbnailUrl(d.id)}
            alt={d.name || d.id}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
        <div className="hub-search__previewtitle">{d.name || "Untitled"}</div>
        <div className="hub-search__previewsub">
          {new Date(d.createdAt * 1000).toLocaleString()}
        </div>
        <button type="button" className="hub-search__previewbtn" onClick={() => onOpenDiagram(d)}>
          Open diagram
        </button>
      </div>
    );
  }
  if (row.kind === "media") {
    const m = row.media;
    return (
      <div className="hub-search__previewinner">
        <div className="hub-search__previewmedia">
          <img src={mediaUrl(m)} alt={m.name || m.id} />
        </div>
        <div className="hub-search__previewtitle">{m.name || "Untitled"}</div>
        {m.description && (
          <div className="hub-search__previewsub">{m.description}</div>
        )}
        <div className="hub-search__previewsub">
          {m.width && m.height ? `${m.width}×${m.height}` : m.mimeType || ""}
        </div>
        {canInsertMedia && (
          <button type="button" className="hub-search__previewbtn" onClick={() => onInsertMedia(m)}>
            Insert into canvas
          </button>
        )}
      </div>
    );
  }
  if (row.kind === "gif") {
    const g = row.gif;
    return (
      <div className="hub-search__previewinner">
        <div className="hub-search__previewmedia">
          <img src={g.url} alt={g.title} />
        </div>
        <div className="hub-search__previewtitle">{g.title || "GIF"}</div>
        <div className="hub-search__previewsub">
          {g.width && g.height ? `${g.width}×${g.height}` : ""} · GIPHY
        </div>
        <button
          type="button"
          className="hub-search__previewbtn"
          disabled={!!inserting}
          onClick={() => onInsertGif(g)}
        >
          {inserting === g.id ? "Inserting…" : "Insert into canvas"}
        </button>
      </div>
    );
  }
  // stencil
  const s: any = row.stencil;
  return (
    <div className="hub-search__previewinner">
      <div className="hub-search__previewmedia hub-search__previewmedia--stencil">
        {stencilPreview ? (
          <img src={stencilPreview} alt={s.name || "stencil"} />
        ) : (
          <span className="hub-libs__preview-ph">▢</span>
        )}
      </div>
      <div className="hub-search__previewtitle">{s.name || "Unnamed stencil"}</div>
      {s.description && (
        <div className="hub-search__previewsub">{s.description}</div>
      )}
      <div className="hub-search__previewsub">
        {((s.elements as any[]) ?? []).length} elements
      </div>
    </div>
  );
};
